import { Pencil, Plus, Trash2, UserX } from 'lucide-react';
import { useState } from 'react';
import type { Swatch, User } from '@bccomms/shared';
import { session } from '../../lib/session';
import { useStore } from '../../store';
import { Field, Modal, SwatchPicker, Toggle } from '../ui';
import { usePanels } from './usePanels';

type Draft = Omit<User, 'id' | 'createdAt'> & { id?: string; password: string };

const blank = (): Draft => ({ username: '', displayName: '', role: 'user', panelId: null, canFeedProgram: false, service: false, color: 'cyan', password: '' });

export function UsersTab() {
  const users = useStore((s) => s.users);
  const presence = useStore((s) => s.presence);
  const me = useStore((s) => s.user);
  const [panels] = usePanels();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      const { password, ...user } = draft;
      await session.admin('admin.users.upsert', { user, password: password || undefined });
      setDraft(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (u: User) => {
    if (!confirm(`Delete user ${u.displayName} (${u.username})?`)) return;
    await session.admin('admin.users.delete', { id: u.id }).catch((e: Error) => alert(e.message));
  };
  const kick = async (u: User) => {
    await session.admin('admin.kick', { userId: u.id }).catch((e: Error) => alert(e.message));
  };

  return (
    <>
      <div className="admin__title">
        <h2>Users</h2>
        <button className="btn btn--primary" onClick={() => setDraft(blank())}>
          <Plus size={16} /> New user
        </button>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Username</th>
            <th>Role</th>
            <th>Panel</th>
            <th>Program</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {users.map((u) => {
            const p = presence[u.id];
            return (
              <tr key={u.id}>
                <td>
                  <span className="row">
                    <span className="dot" data-sw={u.color} style={{ background: 'var(--sw)' }} /> {u.displayName}
                  </span>
                </td>
                <td className="mono">{u.username}</td>
                <td>{u.service ? 'service' : u.role}</td>
                <td>{panels.find((x) => x.id === u.panelId)?.name ?? <span className="muted">none</span>}</td>
                <td>{u.canFeedProgram ? 'yes' : '—'}</td>
                <td>{p?.online ? <span className="chip chip--success">online · {p.client}</span> : <span className="chip">offline</span>}</td>
                <td>
                  <div className="table__actions">
                    {p?.online && u.id !== me?.id && (
                      <button className="iconbtn" aria-label="Disconnect" title="Disconnect" onClick={() => void kick(u)}>
                        <UserX size={16} />
                      </button>
                    )}
                    <button className="iconbtn" aria-label="Edit" title="Edit" onClick={() => setDraft({ ...u, password: '' })}>
                      <Pencil size={16} />
                    </button>
                    {u.id !== me?.id && (
                      <button className="iconbtn" aria-label="Delete" title="Delete" onClick={() => void remove(u)}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {draft && (
        <Modal
          title={draft.id ? 'Edit user' : 'New user'}
          onClose={() => setDraft(null)}
          actions={
            <>
              <button className="btn btn--neutral" onClick={() => setDraft(null)}>
                Cancel
              </button>
              <button className="btn btn--primary" disabled={busy || !draft.username || !draft.displayName || (!draft.id && !draft.password)} onClick={() => void save()}>
                Save
              </button>
            </>
          }
        >
          <Field label="Display name">
            <input className="input" value={draft.displayName} onChange={(e) => setDraft({ ...draft, displayName: e.target.value })} />
          </Field>
          <Field label="Username">
            <input className="input" autoCapitalize="none" value={draft.username} onChange={(e) => setDraft({ ...draft, username: e.target.value })} />
          </Field>
          <Field label={draft.id ? 'New password (leave blank to keep)' : 'Password'} help={draft.id ? 'Changing the password signs the user out everywhere.' : undefined}>
            <input className="input" type="password" value={draft.password} onChange={(e) => setDraft({ ...draft, password: e.target.value })} />
          </Field>
          <Field label="Role">
            <select className="select" value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value as User['role'] })}>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          <Field label="Panel (key layout)">
            <select className="select" value={draft.panelId ?? ''} onChange={(e) => setDraft({ ...draft, panelId: e.target.value || null })}>
              <option value="">None</option>
              {panels.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {p.keys.length} keys
                </option>
              ))}
            </select>
          </Field>
          <Toggle label="May feed program audio (IFB source)" on={draft.canFeedProgram} onChange={(v) => setDraft({ ...draft, canFeedProgram: v })} />
          <Toggle
            label="Service account"
            help="For the Audio Injector and hardware bridges. Doesn't use one of the plan's online seats, and can't talk from a microphone."
            on={draft.service}
            onChange={(v) => setDraft({ ...draft, service: v })}
          />
          <Field label="Colour">
            <SwatchPicker value={draft.color} onChange={(c) => setDraft({ ...draft, color: (c ?? 'cyan') as Swatch })} />
          </Field>
          {error && <p className="error">{error}</p>}
        </Modal>
      )}
    </>
  );
}
